/**
 * Choosing the category tiles that fill the Discovery row when the
 * catalogue can't supply three eligible products.
 *
 * These are a *presentation* fallback only: a tile is a real category from
 * the live tree, linking to that category's real page. It never carries a
 * rating, a review count or any product detail, and it never enters the
 * rotation history — only products the shopper was actually shown do
 * (src/utils/discoveryRotation.js).
 */

/** The main browse page, used when even a tile can't be offered. */
export const EXPLORE_ALL_PATH = "/categories";

const text = (value) => (typeof value === "string" ? value.trim() : "");

/**
 * A department can be offered as a tile only if it is something a shopper
 * can actually land on and see: visible in the nav, not age-gated (the
 * panel has no way to know whether this shopper is verified, and a tile
 * that leads to a gate is not a discovery), and carrying real artwork —
 * there is no placeholder tile.
 */
export const isOfferableCategory = (category) =>
  !!category &&
  !!text(category.slug) &&
  !!text(category.name) &&
  !!text(category.image_url) &&
  category.launch_active !== false &&
  category.is_active !== false &&
  !category.age_restricted;

/** The shape the tile component renders — nothing product-like in it. */
export const categoryTile = (category) => ({
  slug: text(category.slug),
  name: text(category.name),
  imageUrl: text(category.image_url),
  to: `/category/${text(category.slug)}`,
});

/**
 * Up to `needed` unique tiles from the live category tree.
 *
 * `context` is the recommendation context — the category and subcategory
 * slugs of the products actually shown in this row, in display order. A
 * department matches when it, or one of its children, is in that list, and
 * matches lead. Everything else follows in the tree's own order (the API
 * returns departments by display_order), so the choice is deterministic:
 * the same row renders the same tiles on every load of the period, without
 * storing anything.
 *
 * Deduplicated by slug, so two tiles are never the same category.
 */
export function pickFallbackCategories(tree, { needed = 0, context = [] } = {}) {
  if (!(needed > 0)) return [];
  const departments = (Array.isArray(tree) ? tree : []).filter(isOfferableCategory);
  if (!departments.length) return [];

  const wanted = [];
  for (const slug of Array.isArray(context) ? context : []) {
    const key = text(slug);
    if (key && !wanted.includes(key)) wanted.push(key);
  }

  const matches = (department, slug) =>
    text(department.slug) === slug ||
    (Array.isArray(department.children) && department.children.some((child) => text(child?.slug) === slug));

  const picked = [];
  const seen = new Set();
  const take = (department) => {
    const slug = text(department.slug);
    if (seen.has(slug) || picked.length >= needed) return;
    seen.add(slug);
    picked.push(categoryTile(department));
  };

  for (const slug of wanted) {
    if (picked.length >= needed) break;
    const match = departments.find((department) => matches(department, slug));
    if (match) take(match);
  }
  for (const department of departments) {
    if (picked.length >= needed) break;
    take(department);
  }
  return picked;
}

/** The category slugs a row of products puts the shopper in front of —
 *  what the tiles should lean towards. Order is the row's own. */
export function contextSlugsFor(products) {
  const out = [];
  for (const product of Array.isArray(products) ? products : []) {
    for (const slug of [product?.category, product?.subcategory]) {
      const key = text(slug);
      if (key && !out.includes(key)) out.push(key);
    }
  }
  return out;
}
